"use client";

import {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import Image from "next/image";
import Link from "next/link";
import {
  AnimatePresence,
  animate,
  motion,
  useMotionValue,
  useReducedMotion,
  type MotionValue,
} from "framer-motion";
import { ArrowRight, ChevronDown, Menu, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { focusRing } from "@/lib/focus-ring";
import { cn } from "@/lib/utils";
import { openRequestDemo } from "@/components/marketing/shared/MarketingCTA";
import { LandingRequestDemoModal } from "@/components/landing/layouts/lumina/LandingRequestDemoModal";
import {
  institutionalNavItems,
  type InstitutionalNavItem,
} from "@/components/landing/layouts/institutional/data";

/**
 * Canonical public navbar — the institutional landing nav, extracted so every
 * marketing page renders the exact same dark mega-menu UI/UX as the homepage.
 * The component is fully self-contained: it ships its own `--inst-*` token
 * scope and nav CSS via the `<style>` block below, so it renders identically
 * whether it sits inside the landing's `.institutional-root` or on a standalone
 * marketing page. It also mounts the request-demo modal so the "Request access"
 * CTA works on any page that uses this nav.
 *
 * It owns a dark semantic-token scope independent of the surrounding page.
 *
 * The desktop dropdown is a FULL-WIDTH band flush under the bar (Harvey-style):
 * one persistent panel whose inner content cross-fades and height-morphs between
 * Platform and Solutions, so moving between menus never jumps. Flat indexed link
 * rows with a cobalt hover rail, a compact feature card, and a clip-wipe reveal.
 */

const NAV_PANEL_CLOSE_DELAY = 160;

function getInstitutionalNavPanelId(label: string) {
  return `institutional-nav-panel-${label.toLowerCase().replace(/\s+/g, "-")}`;
}

type InstitutionalButtonProps = {
  children: ReactNode;
  href?: string;
  onClick?: () => void;
  variant?: "primary" | "secondary" | "dark";
  className?: string;
};

function InstitutionalButton({
  children,
  href,
  onClick,
  variant = "primary",
  className,
}: InstitutionalButtonProps) {
  const buttonClassName = cn(
    "relative h-11 min-w-[9.5rem] overflow-hidden rounded-[6px] px-5 text-[13px] font-semibold transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-0 motion-reduce:transition-none motion-reduce:hover:translate-y-0",
    variant === "primary" &&
      "institutional-primary-shine border-transparent bg-[var(--zom-accent-primary)] text-[color:var(--zom-on-accent)] shadow-[0_16px_42px_rgba(var(--zom-accent-primary-rgb),0.28)] hover:bg-[var(--zom-cobalt-400)] hover:shadow-[0_18px_50px_rgba(var(--zom-accent-primary-rgb),0.42)]",
    variant === "secondary" &&
      "institutional-border-animate bg-transparent text-[color:var(--inst-button-secondary-text)] hover:text-[color:var(--inst-button-secondary-hover)]",
    variant === "dark" &&
      "institutional-border-animate bg-[var(--zom-black)] text-[var(--zom-public-white)]/88 hover:text-[var(--zom-public-white)]",
    className,
  );

  if (href) {
    return (
      <Button asChild className={buttonClassName}>
        <Link href={href}>{children}</Link>
      </Button>
    );
  }

  return (
    <Button className={buttonClassName} onClick={onClick}>
      {children}
    </Button>
  );
}

/**
 * The full-width dropdown band. One persistent outer panel (keyed stable in the
 * parent so it never remounts on a Platform<->Solutions swap) wraps a measured
 * height wrapper and a cross-fading content tree. The reveal clip-wipes the band
 * open top->bottom; the height morph absorbs the size delta between menus.
 */
function InstitutionalNavPanel({
  item,
  panelHeight,
  measureContent,
  prefersReducedMotion,
  onMouseEnter,
  onMouseLeave,
  onItemClick,
}: {
  item: InstitutionalNavItem;
  panelHeight: MotionValue<number | string>;
  measureContent: (node: HTMLDivElement | null) => void;
  prefersReducedMotion: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onItemClick: () => void;
}) {
  return (
    <motion.div
      role="presentation"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      initial={
        prefersReducedMotion
          ? { opacity: 0 }
          : { opacity: 0, y: -8, clipPath: "inset(0 0 100% 0)" }
      }
      animate={
        prefersReducedMotion
          ? { opacity: 1 }
          : { opacity: 1, y: 0, clipPath: "inset(0 0 0% 0)" }
      }
      exit={
        prefersReducedMotion
          ? { opacity: 0 }
          : { opacity: 0, y: -6, clipPath: "inset(0 0 100% 0)" }
      }
      transition={
        prefersReducedMotion
          ? { duration: 0.12, ease: "linear" }
          : { duration: 0.3, ease: [0.16, 1, 0.3, 1] }
      }
      className="institutional-nav-panel fixed top-[var(--marketing-nav-height,68px)] right-0 left-0 z-[80] hidden overflow-hidden lg:block"
    >
      <motion.div style={{ height: panelHeight }} className="overflow-hidden">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={item.label}
            ref={measureContent}
            id={getInstitutionalNavPanelId(item.label)}
            role="menu"
            aria-label={`${item.label} navigation`}
            initial={
              prefersReducedMotion ? { opacity: 0 } : { opacity: 0, x: 6 }
            }
            animate={{ opacity: 1, x: 0 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, x: -6 }}
            transition={{
              duration: prefersReducedMotion ? 0 : 0.16,
              ease: [0.16, 1, 0.3, 1],
            }}
            className="mx-auto max-w-[1760px] px-[clamp(1.5rem,7vw,9rem)] pt-7 pb-7"
          >
            <div className="inst-nav-eyebrow mb-6">
              <span className="inst-nav-eyebrow-line" aria-hidden />
              <span className="inst-nav-eyebrow-text">{item.label}</span>
            </div>

            <div className="inst-nav-cols">
              {item.subItems.map((subItem, index) => (
                <motion.div
                  key={subItem.label}
                  className="inst-nav-col-cell"
                  initial={prefersReducedMotion ? false : { opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    delay: prefersReducedMotion ? 0 : index * 0.04,
                    duration: prefersReducedMotion ? 0 : 0.22,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                >
                  <Link
                    href={subItem.href}
                    role="menuitem"
                    onClick={onItemClick}
                    className={cn("inst-nav-col group", focusRing)}
                  >
                    <span className="inst-nav-col-index" aria-hidden>
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <span className="inst-nav-col-titlerow">
                      <span className="inst-nav-col-title">
                        {subItem.label}
                      </span>
                      {subItem.badge ? (
                        <span className="inst-nav-badge">{subItem.badge}</span>
                      ) : null}
                      <ArrowRight
                        className="inst-nav-col-arrow h-3.5 w-3.5"
                        aria-hidden
                      />
                    </span>
                    <span className="inst-nav-col-desc">
                      {subItem.description}
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>

            {item.footer ? (
              <Link
                href={item.footer.href}
                role="menuitem"
                onClick={onItemClick}
                className={cn("inst-nav-footer group", focusRing)}
              >
                {item.footer.label}
                <ArrowRight
                  className="h-3 w-3 transition-transform group-hover:translate-x-0.5"
                  aria-hidden
                />
              </Link>
            ) : null}
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
}

export default function InstitutionalNav({
  position = "sticky",
  frameClassName = "max-w-[1760px] pl-[clamp(1.5rem,7vw,9rem)] pr-[clamp(1.5rem,7vw,9rem)]",
}: {
  /**
   * "sticky" (default) is the landing behavior: the nav occupies flow space at
   * the top of its scroll container. "fixed" overlays the viewport and is the
   * right choice for the marketing shells, which already offset their content
   * with `var(--marketing-nav-height)` — the nav republishes that variable so
   * the offset tracks its real height. Fixed also sidesteps `overflow: hidden`
   * / sticky-ancestor pitfalls in those shells.
   */
  position?: "sticky" | "fixed";
  /**
   * Width + gutters for the nav bar's inner frame. Defaults to the standard
   * contained width; the landing passes its wide tight-gutter frame so the logo
   * and CTAs line up with the hero's left/right edges.
   */
  frameClassName?: string;
} = {}) {
  const [open, setOpen] = useState(false);
  const [isNavHidden, setIsNavHidden] = useState(false);
  const [activeNavLabel, setActiveNavLabel] = useState<string | null>(null);
  const navBarRef = useRef<HTMLElement | null>(null);
  const lastScrollYRef = useRef(0);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const navCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRefs = useRef(new Map<string, HTMLButtonElement>());
  const prefersReducedMotion = useReducedMotion() ?? false;
  // Drives the smooth height morph as the dropdown content swaps between menus.
  const panelHeight = useMotionValue<number | string>("auto");
  const activeNavItem =
    institutionalNavItems.find((item) => item.label === activeNavLabel) ?? null;

  // Measure the freshly mounted content and ease the panel toward its height.
  // A ref callback (not useLayoutEffect) fires on the real mount/unmount, so it
  // reads the correct height even though AnimatePresence mode="wait" defers the
  // incoming content by one exit cycle.
  const measureContent = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return;
      const next = node.offsetHeight;
      const current = panelHeight.get();
      // First open (height is "auto") or reduced motion: snap, never animate.
      if (prefersReducedMotion || typeof current !== "number") {
        panelHeight.set(next);
        return;
      }
      if (Math.abs(current - next) < 1) return;
      animate(panelHeight, next, { duration: 0.28, ease: [0.16, 1, 0.3, 1] });
    },
    [panelHeight, prefersReducedMotion],
  );

  const clearNavCloseTimer = useCallback(() => {
    if (navCloseTimerRef.current) {
      clearTimeout(navCloseTimerRef.current);
      navCloseTimerRef.current = null;
    }
  }, []);

  const closeNavPanel = useCallback(() => {
    clearNavCloseTimer();
    setActiveNavLabel(null);
  }, [clearNavCloseTimer]);

  const scheduleNavClose = () => {
    clearNavCloseTimer();
    navCloseTimerRef.current = setTimeout(() => {
      setActiveNavLabel(null);
    }, NAV_PANEL_CLOSE_DELAY);
  };

  const openNavPanel = (item: InstitutionalNavItem) => {
    clearNavCloseTimer();
    setActiveNavLabel(item.label);
  };

  const focusFirstPanelItem = (label: string) => {
    requestAnimationFrame(() => {
      document
        .getElementById(getInstitutionalNavPanelId(label))
        ?.querySelector<HTMLElement>('[role="menuitem"]')
        ?.focus();
    });
  };

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  // Publish the bar height so `var(--marketing-nav-height)` tracks the real nav
  // height — consumed by the full-width dropdown's top offset (every page) and
  // by the marketing shells' content offset (fixed pages).
  useEffect(() => {
    const bar = navBarRef.current;
    if (!bar) return;

    const publish = () => {
      document.documentElement.style.setProperty(
        "--marketing-nav-height",
        `${Math.round(bar.getBoundingClientRect().height)}px`,
      );
    };

    publish();

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(publish);
      resizeObserver.observe(bar);
    }
    window.addEventListener("resize", publish);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", publish);
      document.documentElement.style.removeProperty("--marketing-nav-height");
    };
  }, [position]);

  useEffect(() => {
    return () => clearNavCloseTimer();
  }, [clearNavCloseTimer]);

  useEffect(() => {
    if (open) {
      setIsNavHidden(false);
      return;
    }

    // rAF-throttled, with a direction-aware accumulator: the nav only flips
    // after a sustained run of scrolling one way, so tiny jitter never toggles
    // it. This is what reads as "smooth" rather than twitchy.
    let frame = 0;
    let accumulated = 0;
    const HIDE_AFTER = 72; // px of continued downward scroll before hiding
    const SHOW_AFTER = 24; // px of upward scroll before re-showing

    const evaluate = () => {
      frame = 0;
      const currentScrollY = window.scrollY;
      const delta = currentScrollY - lastScrollYRef.current;
      lastScrollYRef.current = currentScrollY;

      // Near the top the nav is always present.
      if (currentScrollY <= 80) {
        accumulated = 0;
        setIsNavHidden(false);
        return;
      }

      // Reset the accumulator on a direction flip so a reversal counts fresh.
      if ((delta > 0 && accumulated < 0) || (delta < 0 && accumulated > 0)) {
        accumulated = 0;
      }
      accumulated += delta;

      if (accumulated > HIDE_AFTER) {
        accumulated = 0;
        setActiveNavLabel(null);
        setIsNavHidden(true);
      } else if (accumulated < -SHOW_AFTER) {
        accumulated = 0;
        setIsNavHidden(false);
      }
    };

    const handleScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(evaluate);
    };

    lastScrollYRef.current = window.scrollY;
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [open]);

  useEffect(() => {
    if (activeNavLabel) {
      setIsNavHidden(false);
    }
  }, [activeNavLabel]);

  useEffect(() => {
    if (!open && !activeNavLabel) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (activeNavLabel) {
          const trigger = triggerRefs.current.get(activeNavLabel);
          closeNavPanel();
          trigger?.focus();
          return;
        }

        if (open) {
          setOpen(false);
          menuButtonRef.current?.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [activeNavLabel, closeNavPanel, open]);

  return (
    <>
      <Suspense fallback={null}>
        <LandingRequestDemoModal />
      </Suspense>
      <header
        className={cn(
          "institutional-nav-shell top-0 right-0 left-0 z-[70]",
          position === "fixed" ? "fixed" : "sticky",
          isNavHidden && "institutional-nav-hidden",
        )}
      >
        <nav
          ref={navBarRef}
          className={cn(
            "institutional-nav-bar relative z-10 mx-auto h-[68px]",
            frameClassName,
          )}
          aria-label="Primary navigation"
        >
          <Link
            href="/"
            className={cn(
              "institutional-nav-bar__brand group flex items-center",
              focusRing,
            )}
            aria-label="Zom home"
          >
            <Image
              src="/zom-logo-light.png"
              alt="Zom mark"
              width={34}
              height={34}
              className="institutional-brand-logo h-7 w-auto object-contain"
              priority
            />
          </Link>

          <div
            className="institutional-nav-bar__links hidden items-center gap-1 lg:flex"
            onMouseEnter={clearNavCloseTimer}
            onMouseLeave={scheduleNavClose}
          >
            {institutionalNavItems.map((item) =>
              item.panel === "link" ? (
                <div key={item.label} className="relative">
                  <Link
                    href={item.href}
                    onFocus={closeNavPanel}
                    onMouseEnter={closeNavPanel}
                    className={cn(
                      "font-ui flex items-center rounded-[6px] px-3 py-2 text-[14px] font-medium text-[color:var(--inst-nav-link)] transition-colors hover:bg-[color:var(--inst-nav-panel)] hover:text-[color:var(--inst-nav-link-hover)]",
                      focusRing,
                    )}
                  >
                    {item.label}
                  </Link>
                </div>
              ) : (
                <div key={item.label} className="relative">
                  <Button
                    ref={(node) => {
                      if (node) {
                        triggerRefs.current.set(item.label, node);
                        return;
                      }

                      triggerRefs.current.delete(item.label);
                    }}
                    type="button"
                    variant="ghost"
                    aria-haspopup="menu"
                    aria-expanded={activeNavLabel === item.label}
                    aria-controls={getInstitutionalNavPanelId(item.label)}
                    onFocus={() => openNavPanel(item)}
                    onMouseEnter={() => openNavPanel(item)}
                    onClick={() => {
                      if (activeNavLabel === item.label) {
                        closeNavPanel();
                        return;
                      }

                      openNavPanel(item);
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== "ArrowDown") return;
                      event.preventDefault();
                      openNavPanel(item);
                      focusFirstPanelItem(item.label);
                    }}
                    className={cn(
                      // Neutralize the shared Button chrome this trigger never had: its 1px
                      // border, the wider has-[>svg] padding, the 200ms eased transition,
                      // and the press scale. The chevron carries size-3 so the Button's
                      // svg size-4 rule does not enlarge it.
                      "font-ui h-auto items-center gap-1 rounded-[6px] border-0 px-3 py-2 text-[14px] font-medium transition-colors duration-150 ease-in-out active:scale-100 has-[>svg]:px-3",
                      // The open (active) trigger is also the hovered one, so it
                      // needs the same hover:bg or the Button ghost variant's
                      // hover:bg-background-muted outranks the panel colour.
                      activeNavLabel === item.label
                        ? "bg-[color:var(--inst-nav-panel)] text-[color:var(--inst-nav-link-hover)] shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] hover:bg-[color:var(--inst-nav-panel)]"
                        : "text-[color:var(--inst-nav-link)] hover:bg-[color:var(--inst-nav-panel)] hover:text-[color:var(--inst-nav-link-hover)]",
                      focusRing,
                    )}
                  >
                    {item.label}
                    <ChevronDown
                      className={cn(
                        "size-3 transition-transform duration-200",
                        activeNavLabel === item.label && "rotate-180",
                      )}
                    />
                  </Button>
                </div>
              ),
            )}
          </div>

          <div className="institutional-nav-bar__actions hidden items-center gap-3 lg:flex">
            <Link
              href="/login"
              className={cn(
                "font-ui rounded-[6px] border border-[color:var(--inst-nav-border)] bg-[color:var(--inst-nav-panel)] px-4 py-2 text-[12px] font-medium text-[color:var(--inst-ink-soft)] shadow-[0_10px_28px_rgba(0,0,0,0.12)] transition duration-200 hover:-translate-y-0.5 hover:border-[color:var(--inst-line-strong)] hover:text-[color:var(--inst-ink)] motion-reduce:transition-none motion-reduce:hover:translate-y-0",
                focusRing,
              )}
            >
              Log in
            </Link>
            <InstitutionalButton
              variant="primary"
              onClick={() => openRequestDemo("institutional_nav")}
              className="h-10 min-w-0 px-4 text-[12px]"
            >
              Request access
              <ArrowRight className="h-3.5 w-3.5" />
            </InstitutionalButton>
          </div>

          <Button
            ref={menuButtonRef}
            type="button"
            variant="ghost"
            size="icon"
            aria-label={open ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={open}
            aria-controls="institutional-mobile-nav"
            onClick={() => {
              closeNavPanel();
              setOpen((current) => !current);
            }}
            className={cn(
              "institutional-nav-bar__mobile flex h-10 w-10 items-center justify-center rounded-[6px] border border-[color:var(--inst-line)] text-[color:var(--inst-ink)] transition-colors hover:border-[color:var(--inst-line-strong)] hover:bg-[color:var(--inst-nav-hover)] lg:hidden",
              focusRing,
            )}
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </Button>
        </nav>

        <AnimatePresence onExitComplete={() => panelHeight.set("auto")}>
          {activeNavItem ? (
            <InstitutionalNavPanel
              key="institutional-nav-panel"
              item={activeNavItem}
              panelHeight={panelHeight}
              measureContent={measureContent}
              prefersReducedMotion={prefersReducedMotion}
              onMouseEnter={clearNavCloseTimer}
              onMouseLeave={scheduleNavClose}
              onItemClick={closeNavPanel}
            />
          ) : null}
        </AnimatePresence>

        {open ? (
          <div
            id="institutional-mobile-nav"
            className="max-h-[calc(100svh-68px)] overflow-y-auto overscroll-contain border-t border-[color:var(--inst-nav-border)] bg-[color:var(--inst-dropdown-panel)] px-5 py-4 backdrop-blur-2xl lg:hidden"
          >
            <div className="grid gap-2">
              {institutionalNavItems.map((item) => (
                <div key={item.label} className="rounded-[6px]">
                  <Link
                    href={item.href}
                    onClick={() => setOpen(false)}
                    className={cn(
                      "font-ui block rounded-[6px] px-3 py-3 text-sm font-semibold text-[color:var(--inst-ink-soft)] transition-colors hover:bg-[color:var(--inst-nav-hover)] hover:text-[color:var(--inst-ink)]",
                      focusRing,
                    )}
                  >
                    <span className="flex items-center justify-between gap-3">
                      {item.label}
                      <ArrowRight className="h-3.5 w-3.5 text-[color:var(--inst-faint)]" />
                    </span>
                    <span className="mt-1 block text-[12px] leading-relaxed font-normal text-[color:var(--inst-muted)]">
                      {item.summary}
                    </span>
                  </Link>
                  {item.subItems.length > 0 ? (
                    <div className="mt-1 grid gap-1 pl-3">
                      {item.subItems.map((subItem) => (
                        <Link
                          key={subItem.label}
                          href={subItem.href}
                          onClick={() => setOpen(false)}
                          className={cn(
                            "font-ui rounded-[6px] px-3 py-2.5 text-[13px] font-medium text-[color:var(--inst-muted)] transition-colors hover:bg-[color:var(--inst-nav-hover)] hover:text-[color:var(--inst-ink-soft)]",
                            focusRing,
                          )}
                        >
                          <span className="flex flex-wrap items-center gap-2">
                            {subItem.label}
                            {subItem.badge ? (
                              <span className="rounded-[4px] border border-[rgba(var(--zom-accent-primary-rgb),0.46)] px-1.5 py-0.5 font-mono text-[9px] tracking-[0.16em] text-[var(--zom-public-white)]/70 uppercase">
                                {subItem.badge}
                              </span>
                            ) : null}
                          </span>
                          <span className="mt-1 block text-[11px] leading-relaxed font-normal text-[color:var(--inst-faint)]">
                            {subItem.description}
                          </span>
                        </Link>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}
              <div className="mt-2 grid gap-2 border-t border-[color:var(--inst-nav-border)] pt-4">
                <Link
                  href="/login"
                  onClick={() => setOpen(false)}
                  className={cn(
                    "font-ui rounded-[6px] px-3 py-3 text-sm font-medium text-[color:var(--inst-nav-link)] transition-colors hover:bg-[color:var(--inst-nav-hover)] hover:text-[color:var(--inst-ink)]",
                    focusRing,
                  )}
                >
                  Log in
                </Link>
                <InstitutionalButton
                  variant="primary"
                  onClick={() => {
                    setOpen(false);
                    openRequestDemo("institutional_mobile_nav");
                  }}
                  className="w-full"
                >
                  Request access
                </InstitutionalButton>
              </div>
            </div>
          </div>
        ) : null}
      </header>
      <style dangerouslySetInnerHTML={{ __html: institutionalNavStyles }} />
    </>
  );
}

/**
 * Self-contained nav token scope + nav CSS, lifted verbatim from the
 * institutional landing so this nav renders identically anywhere. Tokens are
 * declared on `.institutional-nav-shell` so they cascade to the fixed dropdown
 * panel and the mobile menu (both DOM descendants of the header).
 */
const institutionalNavStyles = `
  .institutional-nav-shell {
    --inst-ink: var(--ld-surface-white);
    --inst-ink-soft: rgba(255, 255, 255, 0.86);
    --inst-muted: rgba(255, 255, 255, 0.5);
    --inst-faint: rgba(255, 255, 255, 0.34);
    --inst-line: rgba(255, 255, 255, 0.11);
    --inst-line-strong: rgba(255, 255, 255, 0.2);
    --inst-black: var(--zom-black);
    --inst-nav-border: rgba(255, 255, 255, 0.11);
    --inst-nav-link: rgba(255, 255, 255, 0.64);
    --inst-nav-link-hover: rgba(255, 255, 255, 0.96);
    --inst-nav-panel: rgba(7, 9, 14, 0.62);
    --inst-nav-hover: rgba(255, 255, 255, 0.06);
    --inst-dropdown-panel: #0a0c12;
    --inst-dropdown-border: rgba(255, 255, 255, 0.13);
    --inst-dropdown-row: rgba(255, 255, 255, 0.034);
    --inst-dropdown-row-hover: rgba(255, 255, 255, 0.074);
    --inst-dropdown-feature-bg: rgba(5, 7, 12, 0.76);
    --inst-button-secondary-text: rgba(255, 255, 255, 0.9);
    --inst-button-secondary-hover: #ffffff;
    --inst-brand-logo-filter: none;
    /* Fully transparent nav, no background/blur/border/shadow ever. It only
       fades and glides out on scroll-down and back in on scroll-up, on the same
       easing curve as the rest of the landing reveals. */
    isolation: isolate;
    background: transparent;
    border: 0;
    box-shadow: none;
    transition:
      transform 0.72s cubic-bezier(0.16, 1, 0.3, 1),
      opacity 0.46s cubic-bezier(0.16, 1, 0.3, 1);
    will-change: transform, opacity;
  }

  .institutional-nav-hidden {
    opacity: 0;
    transform: translateY(-18px);
    pointer-events: none;
  }

  @media (prefers-reduced-motion: reduce) {
    .institutional-nav-shell {
      transition: opacity 0.2s linear;
    }
    .institutional-nav-hidden {
      transform: none;
    }
  }

  /*
   * Bar layout: 3-column grid keeps Platform / Solutions / Security / Company
   * centered in the viewport. Do NOT switch back to flex + justify-between —
   * the CTAs are wider than the logo, which pulls the link group off-center.
   * All marketing pages share this nav via InstitutionalNav.tsx.
   */
  .institutional-nav-shell .institutional-nav-bar {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    width: 100%;
  }

  .institutional-nav-shell .institutional-nav-bar__brand {
    justify-self: start;
  }

  .institutional-nav-shell .institutional-nav-bar__links {
    justify-self: center;
  }

  .institutional-nav-shell .institutional-nav-bar__actions,
  .institutional-nav-shell .institutional-nav-bar__mobile {
    grid-column: 3;
    justify-self: end;
  }

  /* Full-bleed dropdown band, flush under the bar: square edges, top + bottom
     hairline, a single cobalt glow drifting in from the right, deep shadow.
     This SCOPED rule intentionally overrides the landing's softer, unscoped
     .institutional-nav-panel (in InstitutionalLanding.tsx) so the dropdown
     reads as a distinct full-bleed overlay; review both together when restyling. */
  .institutional-nav-shell .institutional-nav-panel {
    isolation: isolate;
    border-radius: 0;
    border-top: 1px solid var(--inst-dropdown-border);
    border-bottom: 1px solid var(--inst-dropdown-border);
    background:
      radial-gradient(ellipse 38% 70% at 88% -10%, rgba(var(--zom-accent-primary-rgb), 0.12), transparent 64%),
      linear-gradient(180deg, rgba(255, 255, 255, 0.045), transparent 26%),
      var(--inst-dropdown-panel);
    -webkit-backdrop-filter: blur(24px) saturate(1.18);
    backdrop-filter: blur(24px) saturate(1.18);
    box-shadow:
      0 30px 90px rgba(0, 0, 0, 0.42),
      inset 0 1px 0 rgba(255, 255, 255, 0.06);
  }

  /* Signature eyebrow: glowing cobalt hairline + mono uppercase label. Reused by
     the column header and the feature card so both read as one system. */
  .institutional-nav-shell .inst-nav-eyebrow {
    display: flex;
    align-items: center;
    gap: 11px;
  }

  .institutional-nav-shell .inst-nav-eyebrow-line {
    display: block;
    width: clamp(0.75rem, 1.1vw, 1.1rem);
    height: 1px;
    flex: 0 0 auto;
    background: linear-gradient(
      90deg,
      rgba(var(--zom-accent-secondary-rgb), 0.95),
      rgba(var(--zom-accent-primary-rgb), 0.28),
      transparent
    );
    box-shadow: 0 0 18px rgba(var(--zom-accent-primary-rgb), 0.28);
  }

  .institutional-nav-shell .inst-nav-eyebrow-text {
    font-family: var(--font-mono);
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--inst-muted);
  }

  /* Routes laid out in a row, divided by hairline separators. Each column is a
     flat link: mono index, title, description; cobalt index + arrow on hover.
     align-items:stretch keeps the separators full height across uneven columns. */
  .institutional-nav-shell .inst-nav-cols {
    display: flex;
    align-items: stretch;
  }

  .institutional-nav-shell .inst-nav-col-cell {
    flex: 1 1 0;
    min-width: 0;
    padding: 0 20px;
  }

  .institutional-nav-shell .inst-nav-col-cell:first-child {
    padding-left: 0;
  }

  .institutional-nav-shell .inst-nav-col-cell:last-child {
    padding-right: 0;
  }

  .institutional-nav-shell .inst-nav-col-cell + .inst-nav-col-cell {
    border-left: 1px solid var(--inst-line);
  }

  .institutional-nav-shell .inst-nav-col {
    display: flex;
    height: 100%;
    flex-direction: column;
    gap: 9px;
  }

  .institutional-nav-shell .inst-nav-col-index {
    font-family: var(--font-mono);
    font-size: 10px;
    font-variant-numeric: tabular-nums;
    color: var(--inst-muted);
    transition: color 0.22s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .institutional-nav-shell .inst-nav-col:hover .inst-nav-col-index {
    color: var(--zom-accent-primary);
  }

  .institutional-nav-shell .inst-nav-col-titlerow {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }

  .institutional-nav-shell .inst-nav-col-title {
    font-family: var(--font-ui);
    font-size: 15px;
    font-weight: 600;
    line-height: 1.2;
    color: var(--inst-ink-soft);
    transition: color 0.22s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .institutional-nav-shell .inst-nav-col:hover .inst-nav-col-title {
    color: var(--inst-ink);
  }

  .institutional-nav-shell .inst-nav-col-arrow {
    flex: 0 0 auto;
    color: var(--zom-accent-primary);
    opacity: 0;
    transform: translateX(-3px);
    transition:
      opacity 0.22s cubic-bezier(0.16, 1, 0.3, 1),
      transform 0.22s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .institutional-nav-shell .inst-nav-col:hover .inst-nav-col-arrow,
  .institutional-nav-shell .inst-nav-col:focus-visible .inst-nav-col-arrow {
    opacity: 1;
    transform: translateX(0);
  }

  .institutional-nav-shell .inst-nav-col-desc {
    font-family: var(--font-ui);
    font-size: 12.5px;
    line-height: 1.55;
    color: var(--inst-muted);
  }

  /* Flat footer: a single full-width hairline above, mono cap label, no box. */
  .institutional-nav-shell .inst-nav-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 18px;
    padding: 14px 0 0;
    border-top: 1px solid var(--inst-line);
    font-family: var(--font-mono);
    font-size: 10px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--inst-muted);
    transition: color 0.22s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .institutional-nav-shell .inst-nav-footer:hover {
    color: var(--inst-ink-soft);
  }

  /* Flat "New" badge cap. */
  .institutional-nav-shell .inst-nav-badge {
    border: 1px solid rgba(var(--zom-accent-primary-rgb), 0.5);
    border-radius: 5px;
    padding: 1px 7px;
    font-family: var(--font-mono);
    font-size: 9px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.72);
  }

  @media (prefers-reduced-motion: reduce) {
    .institutional-nav-shell .inst-nav-col-index,
    .institutional-nav-shell .inst-nav-col-title,
    .institutional-nav-shell .inst-nav-col-arrow,
    .institutional-nav-shell .inst-nav-footer {
      transition: none;
    }
    .institutional-nav-shell .inst-nav-col:hover .inst-nav-col-arrow,
    .institutional-nav-shell .inst-nav-col:focus-visible .inst-nav-col-arrow {
      opacity: 1;
      transform: none;
    }
  }

  .institutional-nav-shell .institutional-brand-logo {
    filter: var(--inst-brand-logo-filter);
  }

  .institutional-nav-shell .institutional-primary-shine::before {
    content: "";
    position: absolute;
    inset: -35% auto -35% -42%;
    width: 38%;
    transform: skewX(-18deg);
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.42), transparent);
    transition: transform 0.72s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .institutional-nav-shell .institutional-primary-shine:hover::before {
    transform: translateX(390%) skewX(-18deg);
  }

  .institutional-nav-shell .institutional-border-animate {
    background:
      linear-gradient(var(--inst-black), var(--inst-black)) padding-box,
      linear-gradient(120deg, rgba(255,255,255,0.22), rgba(var(--zom-accent-secondary-rgb), 0.62), rgba(255,255,255,0.18)) border-box;
    background-size: 100% 100%, 220% 100%;
    border: 1px solid transparent;
    transition:
      background-position 0.68s cubic-bezier(0.16, 1, 0.3, 1),
      box-shadow 0.28s ease,
      transform 0.2s ease,
      color 0.2s ease;
  }

  .institutional-nav-shell .institutional-border-animate:hover {
    background-position: 0 0, 100% 0;
    box-shadow: 0 0 0 1px rgba(var(--zom-accent-secondary-rgb), 0.24), 0 18px 48px rgba(0,0,0,0.34);
  }
`;
